/**
 * Persist UTM / referral parameters for the whole session so that they
 * survive client-side navigation (landing page → /booking → /contact) and
 * can be attached to conversion events and form submissions.
 *
 * Example: a visitor arriving from ChatGPT carries ?utm_source=chatgpt.com.
 */
const KEY = 'physiowrld-utm';
const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'gclid', 'fbclid'] as const;

export type UtmParams = Partial<Record<(typeof UTM_KEYS)[number], string>>;

const isBrowser = typeof window !== 'undefined';

/** Call once on app start (and on every navigation) to capture UTMs from the URL. */
export function captureUtm(): UtmParams {
  if (!isBrowser) return {};
  try {
    const params = new URLSearchParams(window.location.search);
    const found: UtmParams = {};
    UTM_KEYS.forEach((k) => {
      const v = params.get(k);
      if (v) found[k] = v.slice(0, 200);
    });
    if (Object.keys(found).length) {
      // First touch wins for the session unless a new utm_source arrives.
      const prev = getUtm();
      const merged = found.utm_source ? { ...prev, ...found } : { ...found, ...prev };
      sessionStorage.setItem(KEY, JSON.stringify(merged));
      return merged;
    }
    return getUtm();
  } catch {
    return {};
  }
}

export function getUtm(): UtmParams {
  if (!isBrowser) return {};
  try {
    const raw = sessionStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as UtmParams) : {};
  } catch {
    return {};
  }
}

/** Append stored UTMs to an internal path, preserving any existing query. */
export function withUtm(path: string): string {
  const utm = getUtm();
  const entries = Object.entries(utm).filter(([, v]) => !!v) as [string, string][];
  if (!entries.length) return path;
  const [base, hash] = path.split('#');
  const [pathname, search = ''] = base.split('?');
  const qs = new URLSearchParams(search);
  entries.forEach(([k, v]) => {
    if (!qs.has(k)) qs.set(k, v);
  });
  return `${pathname}?${qs.toString()}${hash ? `#${hash}` : ''}`;
}
